import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { apiService } from '../../services/apiService';
import { apiCallWithCache, clearCacheEntry } from '../../utils/apiUtils';
import './AddUserPage.css';
import Select from 'react-select';
import { FaEye } from 'react-icons/fa';
import Cookies from 'js-cookie';
import { 
  hasPermission, 
  hasAnyPermission, 
  filterDataByUserAccess,
  PERMISSION_CODES 
} from '../../utils/permissionUtils';
import { PermissionGuard } from '../../components/PermissionGuard';
import notificationService from '../../CommanService/notificationService';

const AddUserModal = ({ open, onClose, roles, regions, sites, companies, editUser, onUserSaved }) => {
  // All hooks must be called at the top level, before any early return
  const [firstName, setFirstName] = useState(editUser ? editUser.first_name : '');
  const [lastName, setLastName] = useState(editUser ? editUser.last_name : '');
  const [email, setEmail] = useState(editUser ? editUser.email : '');
  const [phone, setPhone] = useState(editUser ? editUser.phone_number : '');
  const [role, setRole] = useState(editUser ? roles.find(r => r.id === editUser.role_id) ? { value: editUser.role_id, label: editUser.role_name } : null : null);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [selectedRegions, setSelectedRegions] = useState(editUser && editUser.regions ? editUser.regions.map(r => ({ value: r.regionID, label: regions.find(reg => reg.id === r.regionID)?.region_name || '' })) : []);
  const [selectedSites, setSelectedSites] = useState(editUser && editUser.siteIDs ? editUser.siteIDs.map(id => ({ value: id, label: sites.find(s => s.id === id)?.site_code || '' })) : []);
  const [status, setStatus] = useState(editUser ? (editUser.is_active ? 'active' : 'inactive') : 'active');
  const [superAdmin, setSuperAdmin] = useState(editUser ? (editUser.is_super_admin ? 'yes' : 'no') : '');
  // Move these hooks to the top, outside of any conditionals
  const [regionDisabled, setRegionDisabled] = useState(false);
  const [siteDisabled, setSiteDisabled] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [superAdminError, setSuperAdminError] = useState('');
  const [regionSiteError, setRegionSiteError] = useState('');
  const [selectedCompany, setSelectedCompany] = useState(editUser ? (editUser.company_id ? { value: editUser.company_id, label: editUser.company_name } : null) : null);

  // Fix: Helper to get region option from region id
  const getRegionOption = (regionId) => {
    const region = regions.find(r => r.id === regionId);
    return region ? { value: region.id, label: region.region_name } : null;
  };

  // Fix: Helper to get site option from site id
  const getSiteOption = (siteId) => {
    const site = sites.find(s => s.id === siteId);
    return site ? { value: site.id, label: site.site_code } : null;
  };

  // Fix: Initialize selectedRegions and selectedSites correctly in edit mode
  useEffect(() => {
    if (editUser) {
      setFirstName(editUser.first_name || '');
      setLastName(editUser.last_name || '');
      setEmail(editUser.email || '');
      setPhone(editUser.phone_number || '');
      setRole(editUser.role_id ? { value: editUser.role_id, label: editUser.role_name } : null);
      // If regions are present and not regionID: 0, pre-select regions
      if (editUser.regions && editUser.regions.length > 0 && editUser.regions[0].regionID !== 0) {
        setSelectedRegions(editUser.regions.map(r => getRegionOption(r.regionID)).filter(Boolean));
        setSelectedSites([]);
        setSiteDisabled(true);
        setRegionDisabled(false);
      } else if (editUser.siteIDs && editUser.siteIDs.length > 0) {
        setSelectedSites(editUser.siteIDs.map(id => getSiteOption(id)).filter(Boolean));
        setSelectedRegions([]);
        setRegionDisabled(true);
        setSiteDisabled(false);
      } else {
        setSelectedRegions([]);
        setSelectedSites([]);
        setRegionDisabled(false);
        setSiteDisabled(false);
      }
      setStatus(editUser.is_active ? 'active' : 'inactive');
      setSuperAdmin(editUser.is_super_admin ? 'yes' : 'no');
      setPassword(editUser.password || '');
      setConfirmPassword(editUser.password || '');
      setSelectedCompany(editUser.company_id ? { value: editUser.company_id, label: editUser.company_name } : null);
    } else {
      setSelectedCompany(null);
    }
  }, [editUser, regions, sites]);

  if (!open) return null;

  // Prepare options
  const roleOptions = roles.map(r => ({ value: r.id, label: r.role_name }));
  const regionOptions = regions.map(r => ({ value: r.id, label: r.region_name }));
  // Filter site options based on selected regions
  const filteredSiteOptions = selectedRegions.length > 0
    ? sites
        .filter(s => selectedRegions.some(r => r.value === s.region_id))
        .map(s => ({ value: s.id, label: s.site_code }))
    : sites.map(s => ({ value: s.id, label: s.site_code }));
  const companyOptions = companies ? companies.map(c => ({ value: c.id, label: c.company_name })) : [];

  const handleRegionChange = (vals) => {
    // Check if "Select All" is selected
    if (vals && vals.some(v => v.value === 'all')) {
      // Select all regions except the "Select All" option itself
      const allRegionOptions = regionOptions;
      setSelectedRegions(allRegionOptions);
      setSelectedSites([]);
      setSiteDisabled(true);
    } else {
      setSelectedRegions(vals || []);
      if (vals && vals.length > 0) {
        setSelectedSites([]);
        setSiteDisabled(true);
      } else {
        setSiteDisabled(false);
      }
    }
  };
  const handleSiteChange = (vals) => {
    // Check if "Select All" is selected
    if (vals && vals.some(v => v.value === 'all')) {
      // Select all sites except the "Select All" option itself
      const allSiteOptions = filteredSiteOptions;
      setSelectedSites(allSiteOptions);
      setSelectedRegions([]);
      setRegionDisabled(true);
    } else {
      setSelectedSites(vals || []);
      if (vals && vals.length > 0) {
        setSelectedRegions([]);
        setRegionDisabled(true);
      } else {
        setRegionDisabled(false);
      }
    }
  };

  // Password validation rules
  const isLengthValid = password.length >= 8 && password.length <= 16;
  const isCaseValid = /[a-z]/.test(password) && /[A-Z]/.test(password);
  const isNumberValid = /[0-9]/.test(password);
  const isSpecialValid = /[@$!%*#?&]/.test(password);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setPasswordError('');
    setSuperAdminError('');
    setRegionSiteError('');
    if (!selectedCompany) {
      notificationService.showWarning('Please select a company.');
      return;
    }
    // Status validation
    if (!status) {
      notificationService.showWarning('Please select a status.');
      return;
    }
    if (password !== confirmPassword) {
      setPasswordError('Password and Confirm Password do not match');
      return;
    }
    // if (!superAdmin) {
    //   notificationService.showWarning('Please select Super Admin.');
    //   return;
    // }
    debugger
    const role_id = role ? role.value : null;
    const is_active = status === 'active';
    // const is_super_admin = superAdmin === 'yes';
    let is_super_admin;
    if (editUser) {
      is_super_admin = editUser.is_super_admin;
    } else {
      is_super_admin =false;
    }

    let regionsArr = [];
    let siteIDsArr = [];
    if (selectedRegions.length > 0) {
      // Regions selected: send all sites for each region
      regionsArr = selectedRegions.map(region => ({
        regionID: region.value,
        siteIDs: sites.filter(site => site.region_id === region.value).map(site => site.id)
      }));
      siteIDsArr = [];
    } else {
      // Only sites selected: send regionID: 0 and all selected site IDs
      regionsArr = [{
        regionID: 0,
        siteIDs: selectedSites.map(site => site.value)
      }];
      siteIDsArr = [];
    }
    debugger
    const payload = {
      id: editUser ? editUser.id : undefined,
      first_name: firstName,
      last_name: lastName,
      email: email.toLowerCase(),
      password,
      phone_number: phone,
      role_id,
      is_active,
      is_super_admin,
      regions: regionsArr,
      siteIDs: siteIDsArr,
      company_id: selectedCompany.value,
      company_name: selectedCompany.label,
    };
    console.log('Payload being sent to API:', payload);
    try {
     
      const response = await apiService.addOrUpdateUser(payload);
     
       if(response.success===false){
        notificationService.showWarning(response.message);
        return;
       }
      onClose();
      console.log('User saved successfully, calling onUserSaved callback...');
      if (onUserSaved) onUserSaved();
      notificationService.showSuccess(editUser ? 'User updated successfully!' : 'User added successfully!');
    } catch (error) {
      console.error(error);
      notificationService.showError('Failed to add/update user.');
    }
  };

  return (
    <div className="modal-backdrop">
      <div className="add-user-modal">
        <button className="modal-close" onClick={onClose}>✖</button>
        <h2>{editUser ? 'Edit User' : 'Add User'}</h2>
        <form onSubmit={handleSubmit} autoComplete="off">
          <div className="add-user-modal-row">
            <div>
              <label><span>First Name <span style={{color: 'red'}}>*</span></span></label>
              <input value={firstName} onChange={e => setFirstName(e.target.value)} required name="firstName" autoComplete="off" />
            </div>
            <div>
              <label><span>Last Name <span style={{color: 'red'}}>*</span></span></label>
              <input value={lastName} onChange={e => setLastName(e.target.value)} required name="lastName" autoComplete="off" />
            </div>
          </div>
          <div className="add-user-modal-row">
            <div>
              <label><span>Email ID <span style={{color: 'red'}}>*</span></span></label>
              <input value={email} onChange={e => setEmail(e.target.value)} required name="new-user-email" autoComplete="off" />
            </div>
            <div>
              <label><span>Phone No <span style={{color: 'red'}}>*</span></span></label>
              <input value={phone} onChange={e => setPhone(e.target.value)} minLength={10} maxLength={13} name="phoneNumber" autoComplete="off" />
            </div>
          </div>
          <div className="add-user-modal-row">
            <div>
              <label><span>Company <span style={{color: 'red'}}>*</span></span></label>
              <Select classNamePrefix="react-select" options={companyOptions} value={selectedCompany} onChange={setSelectedCompany} placeholder="Select Company" />
            </div>
            <div>
              <label><span>Role <span style={{color: 'red'}}>*</span></span></label>
              <Select classNamePrefix="react-select" options={roleOptions} value={role} onChange={setRole} placeholder="Select Role" />
            </div>
          </div>
          <div className="add-user-modal-row">
            <div>
              <label><span>Password <span style={{color: 'red'}}>*</span></span></label>
              <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  required
                  style={{ paddingRight: 30 }}
                  name="new-user-password"
                  autoComplete="new-password"
                />
                <span
                  style={{ position: 'absolute', right: 8, cursor: 'pointer', color: '#888' }}
                  onMouseEnter={() => setShowPassword(true)}
                  onMouseLeave={() => setShowPassword(false)}
                  tabIndex={-1}
                >
                  <FaEye size={16} />
                </span>
              </div>
            </div>
          </div>
          <div className="add-user-modal-row">
            <div>
              <label><span>Confirm Password <span style={{color: 'red'}}>*</span></span></label>
              <input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} required name="new-user-confirm-password" autoComplete="new-password" />
              {passwordError && <div style={{ color: 'red', fontSize: 12 }}>{passwordError}</div>}
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8 }}>
              <div className="password-rules">
                <div className={isLengthValid ? 'valid' : ''}>✓ At least minimum 8 characters and maximum 16 characters</div>
                <div className={isCaseValid ? 'valid' : ''}>✓ Includes upper and lower case letters</div>
                <div className={isNumberValid ? 'valid' : ''}>✓ At least one number</div>
                <div className={isSpecialValid ? 'valid' : ''}>✓ At least one special character (@, $, !, %, etc.)</div>
              </div>
            </div>
          </div>
          <div className="add-user-modal-row">
            {/* <div>
              <label>Region*</label>
              <Select
                classNamePrefix="react-select"
                isMulti
                isDisabled={regionDisabled}
                options={[{ value: 'all', label: 'Select All' }, ...regionOptions]}
                value={selectedRegions}
                onChange={handleRegionChange}
                placeholder="Select Region"
              />
            </div> */}
            {/* <div>
              <label>Site*</label>
              <Select
                classNamePrefix="react-select"
                isMulti
                isDisabled={siteDisabled}
                options={[{ value: 'all', label: 'Select All' }, ...filteredSiteOptions]}
                value={selectedSites}
                onChange={handleSiteChange}
                placeholder="Select Site"
              />
            </div> */}
          </div>
          <div className="add-user-modal-row">
            <div>
              <label><span>Status <span style={{color: 'red'}}>*</span></span></label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 4 }}>
                <span style={{ fontWeight: 500, minWidth: 48 }}>
                  {status === 'active' ? 'Active' : 'In Active'}
                </span>
                <label className="switch">
                  <input
                    type="checkbox"
                    checked={status === 'active'}
                    onChange={e => setStatus(e.target.checked ? 'active' : 'inactive')}
                  />
                  <span className="slider round"></span>
                </label>
              </div>
            </div>
            {/* <div>
              <label><span>Super Admin <span style={{color: 'red'}}>*</span></span></label>
              <select value={superAdmin} onChange={e => setSuperAdmin(e.target.value)}>
                <option value="" disabled>Select Super Admin</option>
                <option value="yes">Yes</option>
                <option value="no">No</option>
              </select>
            </div> */}
          </div>
          <div className="modal-actions">
            <button type="button" onClick={onClose}>Cancel</button>
            <button type="submit">{editUser ? 'Update' : 'Save'}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

const cssLoader = (
  <div style={{
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100vw',
    height: '100vh',
    background: 'rgba(255,255,255,0.8)',
    zIndex: 9999,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '20px'
  }}>
    <div style={{ fontSize: '18px', fontWeight: '600', color: '#333' }}>
      Loading Users...
    </div>
    <div className="bouncing-loader">
      <div></div><div></div><div></div>
    </div>
    <style>
      {`
      .bouncing-loader {
        display: flex;
        align-items: flex-end;
        justify-content: center;
        height: 60px;
        gap: 10px;
      }
      .bouncing-loader div {
        width: 18px;
        height: 18px;
        background: linear-gradient(135deg, #ff6600 40%, #ffa500 100%);
        border-radius: 50%;
        animation: bouncing-loader 0.6s infinite alternate;
        box-shadow: 0 4px 16px rgba(255, 102, 0, 0.2);
      }
      .bouncing-loader div:nth-child(2) {
        animation-delay: 0.2s;
      }
      .bouncing-loader div:nth-child(3) {
        animation-delay: 0.4s;
      }
      @keyframes bouncing-loader {
        to {
          transform: translateY(-30px) scale(1.1);
          box-shadow: 0 16px 32px rgba(255, 102, 0, 0.3);
        }
      }
      `}
    </style>
  </div>
);

const AddUserPage = () => {
  const { user } = useSelector(state => state.auth);
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [roles, setRoles] = useState([]);
  const [allSites, setAllSites] = useState(null);
  const [allRegions, setAllRegions] = useState(null);
  const [usersLoaded, setUsersLoaded] = useState(false);
  const [rolesLoaded, setRolesLoaded] = useState(false);
  const [usersLoading, setUsersLoading] = useState(true);
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const isMountedRef = useRef(true);
  const dispatch = useDispatch();
  const [companies, setCompanies] = useState([]);

  // Check if user has permission to access user management
  const canViewUsers = hasPermission(user, PERMISSION_CODES.USER_VIEW) || 
                      hasPermission(user, PERMISSION_CODES.USER_MANAGEMENT);
  const canAddUsers = hasPermission(user, PERMISSION_CODES.USER_ADD) || 
                     hasPermission(user, PERMISSION_CODES.USER_MANAGEMENT);
  const canEditUsers = hasPermission(user, PERMISSION_CODES.USER_EDIT) || 
                      hasPermission(user, PERMISSION_CODES.USER_MANAGEMENT);
  const canDeleteUsers = hasPermission(user, PERMISSION_CODES.USER_DELETE) || 
                        hasPermission(user, PERMISSION_CODES.USER_MANAGEMENT);

  // Fetch users from API

  useEffect(() => {
    isMountedRef.current = true;
    const fetchData = async () => {
      try {
        await alluser();
        await new Promise(resolve => setTimeout(resolve, 100));
        await fetchRoles();
        await new Promise(resolve => setTimeout(resolve, 100));
        await fetchAllSites();
        await new Promise(resolve => setTimeout(resolve, 100));
        await fetchAllRegions();
        // Fetch companies
        const companyRes = await apiService.GetAllCompanies(false);
        setCompanies(Array.isArray(companyRes.data) ? companyRes.data : []);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    fetchData();
    
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const alluser = async () => {
    if (!isMountedRef.current) return;
    
    console.log('Fetching users from API...');
    setUsersLoading(true);
    try {
      const response = await apiCallWithCache('GetAllUsers', () => apiService.GetAllUsers());
      console.log('API response:', response);
      if (isMountedRef.current) {
        // Handle case where response might be null due to cache
        if (response && response.data) {
          // Filter users based on user's access
          const filteredUsers = filterDataByUserAccess(Array.isArray(response.data) ? response.data : [], user);
          console.log('Filtered users:', filteredUsers);
          setUsers(filteredUsers);
        } else {
          console.log('Users already cached, skipping...');
        }
      }
    } catch (err) {
      if (isMountedRef.current) {
        setUsers([]);
        console.error("Error fetching users:", err);
      }
    } finally {
      if (isMountedRef.current) {
        setUsersLoading(false);
      }
    }
  };


  const fetchRoles = async () => {
    if (!isMountedRef.current) return;
    
    try {
      const response = await apiCallWithCache('GetAllRoles', () => apiService.GetAllRoles(false));
      if (isMountedRef.current) {
        // Handle case where response might be null due to cache
        if (response && response.data) {
          setRoles(response.data);
        } else {
          console.log('Roles already cached, skipping...');
        }
      }
    } catch (err) {
      if (isMountedRef.current) {
        setRoles([]);
        console.error("Error fetching roles:", err);
      }
    }
  };

  const fetchAllSites = async () => {
    if (!isMountedRef.current) return;
    
    try {
        const response = await apiCallWithCache('GetAllSites', () => apiService.GetAllSites());
        if (isMountedRef.current) {
          // Handle case where response might be null due to cache
          if (response && response.data) {
            setAllSites(response.data);
          } else {
            console.log('Sites already cached, skipping...');
          }
        }
    } catch (err) {
      if (isMountedRef.current) {
        setAllSites(null);
        console.error("Error fetching master data:", err);
      }
    }
  };

  const fetchAllRegions = async () => {
    if (!isMountedRef.current) return;
    
    try {
        const response = await apiCallWithCache('GetAllRegions', () => apiService.GetAllRegions());
        if (isMountedRef.current) {
          // Handle case where response might be null due to cache
          if (response && response.data) {
            setAllRegions(response.data);
          } else {
            console.log('Regions already cached, skipping...');
          }
        }
    } catch (err) {
      if (isMountedRef.current) {
        setAllRegions(null);
        console.error("Error fetching master data:", err);
      }
    }
  };

  // Filter users by search
  const filteredUsers = users.filter(user =>
    String(user.id).includes(search) ||
    `${user.first_name} ${user.last_name}`.toLowerCase().includes(search.toLowerCase()) ||
    user.email?.toLowerCase().includes(search.toLowerCase()) ||
    user.company_name?.toLowerCase().includes(search.toLowerCase()) ||
    user.phone_number?.toLowerCase().includes(search.toLowerCase()) ||
    user.role_name?.toLowerCase().includes(search.toLowerCase())
  );

  // Pagination logic
  const total = filteredUsers.length;
  const totalPages = Math.ceil(total / rowsPerPage);
  const startIdx = (page - 1) * rowsPerPage;
  const endIdx = Math.min(startIdx + rowsPerPage, total);
  const paginatedUsers = filteredUsers.slice(startIdx, endIdx);

  const handlePrev = () => setPage(p => Math.max(1, p - 1));
  const handleNext = () => setPage(p => Math.min(totalPages, p + 1));
  const handleRowsPerPage = e => {
    setRowsPerPage(Number(e.target.value));
    setPage(1);
  };

  const reloadUsers = async () => {
    console.log('Reloading users...');
    // Clear the cache for GetAllUsers to force a fresh API call
    clearCacheEntry('GetAllUsers');
    await alluser();
    console.log('Users reloaded successfully');
  };

  return (
    <div className="add-user-container">
      {usersLoading && cssLoader}
      <div className="user-header-bar">
        <input
          type="text"
          className="user-search"
          placeholder="Search "
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ width: '350px' }}
        />
        <PermissionGuard permission={PERMISSION_CODES.USER_ADD} fallback={null}>
          <button className="add-user-btn" onClick={() => { setShowAddUserModal(true); setEditUser(null); }}>+ Add User</button>
        </PermissionGuard>
      </div>
      <div className="user-table-wrapper">
        <table className="user-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Name</th>
              <th>Email</th>
              <th>Company</th>
              <th>Phone No</th>
              <th>Role</th>
              <th>Status</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {usersLoading ? (
              <tr><td colSpan={8} style={{ textAlign: 'center' }}>Loading...</td></tr>
            ) : (
              paginatedUsers.length === 0 ? (
                <tr><td colSpan={8} style={{ textAlign: 'center' }}>No users found.</td></tr>
              ) : (
                paginatedUsers.map(user => (
                  <tr key={user.id}>
                    <td>{user.id}</td>
                    <td>{user.first_name} {user.last_name}</td>
                    <td>{user.email}</td>
                    <td>{user.company_name}</td>
                    <td>{user.phone_number}</td>
                    <td>{user.role_name}</td>
                    <td>
                      {user.is_active ? (
                        <span className="status-badge active">Active</span>
                      ) : (
                        <span className="status-badge inactive">In Active</span>
                      )}
                    </td>
                    <td>
                      <PermissionGuard permission={PERMISSION_CODES.USER_EDIT} fallback={null}>
                        <button className="action-btn edit" title="Edit" onClick={() => { setEditUser(user); setShowAddUserModal(true); }}><span role="img" aria-label="edit">✏️</span></button>
                      </PermissionGuard>
                    </td>
                  </tr>
                ))
              )
            )}
          </tbody>
        </table>
        {/* Pagination Controls */}
        {!usersLoading && (
          <div className="user-table-footer">
            <div className="rows-per-page">
              Rows per page:
              <select value={rowsPerPage} onChange={handleRowsPerPage}>
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
              </select>
            </div>
            <div className="pagination-info">
              {total === 0 ? '0' : `${startIdx + 1}-${endIdx}`} of {total}
            </div>
            <div className="pagination-controls">
              <button onClick={handlePrev} disabled={page === 1}>{'<'}</button>
              <button onClick={handleNext} disabled={page === totalPages || total === 0}>{'>'}</button>
            </div>
          </div>
        )}
      </div>
      {showAddUserModal && (
        <AddUserModal
          open={showAddUserModal}
          onClose={() => { setShowAddUserModal(false); setEditUser(null); }}
          roles={roles}
          regions={allRegions || []}
          sites={allSites || []}
          companies={companies}
          editUser={editUser}
          onUserSaved={reloadUsers}
        />
      )}
    </div>
  );
};

export default AddUserPage; 